<template>
  <div data-testid="financial-close-top-nav" class="fc-top-nav">
    <div data-testid="financial-close-top-nav__left" class="fc-top-nav__left">
      <v-menu
        v-if="shortcutsEnabled"
        data-testid="financial-close-top-nav__shortcuts-menu"
        v-model="shortcutsMenu"
        offset-y
        :nudge-bottom="6"
        :close-on-content-click="false"
        content-class="fc-cycle-switcher__menu"
        transition="fade-transition"
      >
        <template #activator="{ on, attrs }">
          <v-tooltip bottom content-class="tooltip-bottom">
            <template #activator="{ on: ton, attrs: tattrs }">
              <button
                data-testid="financial-close-top-nav__shortcuts-btn"
                type="button"
                class="fc-top-nav__icon-btn"
                v-bind="{ ...attrs, ...tattrs }"
                v-on="{ ...on, ...ton }"
                aria-label="Shortcuts"
              >
                <img
                  src="/img/navicon/Shortcuts.svg"
                  alt="Shortcuts"
                  class="fc-top-nav__shortcuts-icon"
                />
              </button>
            </template>
            <span>Shortcuts</span>
          </v-tooltip>
        </template>

        <v-card flat class="fc-cycle-switcher__card pa-2">
          <h5 class="fc-top-nav__shortcuts-title mb-1 font-weight-medium px-2 pt-1">
            Shortcuts
          </h5>
          <shortcuts-menu
            ref="shortcutsMenuComponent"
            :mini="false"
            component="financialclose"
            @shortcut-click="shortcutsMenu = false"
          />
        </v-card>
      </v-menu>

      <cycle-breadcrumb
        :cycle-label="cycleBreadcrumbLabel"
        :selected-client-id="selectedClientId"
        :cycles="cyclesForSwitcher"
        :active-cycle-id="resolvedCycleId"
        :cycles-loading="cyclesLoading"
        @cycle-selected="onCycleSelected"
        @open-cycle-menu="ensureCyclesLoaded"
      />
    </div>

    <div data-testid="financial-close-top-nav__actions" class="fc-nav-actions">
      <AskNoaButton
        v-if="chatbotEnabled"
        test-id="financial-close-top-nav__ask-noa"
        @click="openAskNoa"
      />

      <v-menu
        v-if="canAccessSettings"
        data-testid="financial-close-top-nav__settings-menu"
        v-model="settingsMenu"
        offset-y
        left
        :nudge-bottom="6"
        content-class="fc-setup-menu"
        transition="fade-transition"
        :z-index="400"
      >
        <template #activator="{ on, attrs }">
          <v-tooltip bottom content-class="tooltip-bottom">
            <template #activator="{ on: ton, attrs: tattrs }">
              <button
                data-testid="financial-close-top-nav__settings-btn"
                type="button"
                class="fc-nav-actions__icon-btn"
                :class="{ 'fc-nav-actions__icon-btn--active': isSettingsActive }"
                v-bind="{ ...attrs, ...tattrs }"
                v-on="{ ...on, ...ton }"
                aria-label="Settings"
                aria-haspopup="true"
                :aria-expanded="String(settingsMenu)"
              >
                <img
                  src="/img/icons/SetupIcon.svg"
                  alt="Settings"
                  data-testid="financial-close-top-nav__settings-icon"
                  class="fc-nav-actions__settings-icon"
                />
              </button>
            </template>
            <span>Settings</span>
          </v-tooltip>
        </template>

        <div
          data-testid="financial-close-top-nav__setup-panel"
          class="fc-setup-menu__panel"
        >
          <div
            data-testid="financial-close-top-nav__setup-title"
            class="fc-setup-menu__title"
          >
            Setup
          </div>
          <button
            v-for="(item, index) in settingsItems"
            :key="item.href || item.name || index"
            :data-testid="`financial-close-top-nav__setup-item--${index}`"
            type="button"
            class="fc-setup-menu__item"
            :class="{ 'fc-setup-menu__item--active': isSettingsItemActive(item) }"
            @click="goToSettings(item)"
          >
            {{ settingsDisplayName(item) }}
          </button>
        </div>
      </v-menu>
    </div>
  </div>
</template>

<script>
import CycleBreadcrumb from './CycleBreadcrumb.vue';
import AskNoaButton from './AskNoaButton.vue';
import ShortcutsMenu from '@/components/FinancialClose/ShortcutsMenu.vue';
import {
  extractCycleIdFromRoute,
  getCycleBreadcrumbLabel,
  getCycleDescription,
  buildCycleSwitchPath,
  isFbcSettingsRoute,
} from './financialCloseTopNavUtils';
import {
  FC_MENU_NAMES,
  FC_ROUTES,
  getSettingsNavDisplayName,
} from '@/modules/financialclose/utils/constants';
import constants from '@/plugins/constants';

export default {
  name: 'FinancialCloseTopNav',
  components: { CycleBreadcrumb, AskNoaButton, ShortcutsMenu },
  props: {
    cycleId: { type: String, default: null },
    chatbotEnabled: { type: Boolean, default: false },
    applist: { type: Array, default: () => [] },
    canAccessSettings: { type: Boolean, default: false },
    selectedClientId: { type: String, default: '' },
  },
  emits: ['open-settings', 'cycle-selected', 'shortcut-added', 'ask-noa'],
  data() {
    return {
      shortcutsMenu: false,
      settingsMenu: false,
    };
  },
  computed: {
    shortcutsEnabled() {
      return this.$store.state.pinCount !== -1;
    },
    fcStore() {
      return this.$store.state.m_financialclose || {};
    },
    cyclesForSwitcher() {
      return this.fcStore.cyclesForSwitcher || [];
    },
    cyclesLoading() {
      return Boolean(this.fcStore.cyclesForSwitcherLoading);
    },
    resolvedCycleId() {
      return this.cycleId || extractCycleIdFromRoute(this.$route);
    },
    activeCycleFromStore() {
      const list = this.fcStore.CycleList || [];
      return list.find((c) => c._id === this.resolvedCycleId) || null;
    },
    activeCycleFromSwitcher() {
      return (
        (this.cyclesForSwitcher || []).find((c) => c._id === this.resolvedCycleId) || null
      );
    },
    cycleBreadcrumbLabel() {
      if (!this.resolvedCycleId) return '';
      const cycle = this.activeCycleFromSwitcher || this.activeCycleFromStore;
      return getCycleBreadcrumbLabel(cycle) || cycle?.name || '';
    },
    isSettingsActive() {
      const path = this.$route?.path || '';
      return (
        isFbcSettingsRoute(path) && !path.includes(FC_ROUTES.FS_PREPARATION_PATH_INFIX)
      );
    },
    settingsItems() {
      const masters = (this.$store.state.m_financialclose?.menu || []).find(
        (m) => m.name === FC_MENU_NAMES.MASTERS
      );
      return (masters?.items || []).filter((item) => item?.href);
    },
  },
  watch: {
    resolvedCycleId: {
      immediate: true,
      handler(id) {
        if (id && !this.cycleBreadcrumbLabel) {
          this.ensureCyclesLoaded();
        }
      },
    },
  },
  mounted() {
    if (this.shortcutsEnabled) {
      this.$store.dispatch('shortcuts/fetchShortcuts', {
        component: 'financialclose',
      });
    }
  },
  methods: {
    async ensureCyclesLoaded() {
      await this.$store.dispatch('fetchCyclesForSwitcher');
    },
    onCycleSelected(cycle) {
      if (!cycle?._id) return;
      const nextId = cycle._id;
      const currentId = this.resolvedCycleId;
      if (nextId === currentId) return;

      this.$emit('cycle-selected', cycle);

      const description = getCycleDescription(cycle) || cycle.name || 'Cycle';
      this.$store.commit('addItemToCycleList', {
        _id: nextId,
        description,
      });

      try {
        localStorage.setItem(
          constants.STORAGE_KEYS.USER_OBJ,
          JSON.stringify({ ...cycle, description }),
        );
      } catch (e) {
        // ignore storage failures
      }

      const path = buildCycleSwitchPath(this.$route?.path || '', nextId, currentId)
        || FC_ROUTES.CHECKLIST(nextId);
      // Keep cid / view, but drop prior-cycle userType so the new cycle can resolve Manager/Preparer.
      const query = { ...(this.$route?.query || {}) };
      delete query.userType;
      const cid = this.selectedClientId || this.$store.state.client;
      if (cid) query.cid = cid;

      this.$root.closeFloatingComponents?.();
      this.$nova.gotoLink({ path, query });
    },
    openAskNoa() {
      this.$emit('ask-noa');
      // toggleChatbot syncs projectId/taskId from the current FC route
      this.$root.toggleChatbot?.();
    },
    settingsDisplayName(item) {
      if (item?.edit === true) {
        return this.$store.state.LocationHeader || item.name;
      }
      return getSettingsNavDisplayName(item?.name) || '';
    },
    isSettingsItemActive(item) {
      if (!item?.href) return false;
      const href = item.href.replace(/\/$/, '');
      const path = (this.$route.path || '').replace(/\/$/, '');
      return path === href || path.startsWith(`${href}/`);
    },
    goToSettings(item) {
      if (!item?.href) return;
      this.settingsMenu = false;
      this.$emit('open-settings');
      this.$root.closeFloatingComponents?.(item.name);
      this.$nova.gotoLink({
        path: item.href,
        query: this.selectedClientId ? { cid: this.selectedClientId } : undefined,
      });
    },
  },
  beforeDestroy() {
    this.shortcutsMenu = false;
    this.settingsMenu = false;
  },
};
</script>

<style lang="scss">
/* Unscoped: v-menu content is teleported outside the component root */
@import './FinancialCloseTopNav.scss';
@import './FinancialCloseNavActions.scss';
@import './FinancialCloseSetupMenu.scss';
</style>
